import { useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate, Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "./lib/AuthContext";
import { useCart } from "./lib/CartContext";
import { useSiteClub } from "./lib/SiteClubContext";
import { getLivestreamStatus, getMe } from "./lib/api";
import {
  BoxArrowRightIcon,
  Calendar3RangeFillIcon,
  CameraVideoIcon,
  CarFrontFillIcon,
  CartFillIcon,
  ClipboardCheckIcon,
  FileEarmarkArrowUpIcon,
  FilePersonIcon,
  GeoAltIcon,
  HouseFillIcon,
  KeyFillIcon,
  NewspaperIcon,
  PersonLinesFillIcon,
  PersonRaisedHandIcon,
  ShopIcon,
} from "./components/icons";
import LoginPage from "./pages/LoginPage";
import CallbackPage from "./pages/CallbackPage";
import EventsPage from "./pages/EventsPage";
import HomePage from "./pages/HomePage";
import EventDetailPage from "./pages/EventDetailPage";
import ArticlesPage from "./pages/ArticlesPage";
import ArticleDetailPage from "./pages/ArticleDetailPage";
import LocationsPage from "./pages/LocationsPage";
import HobbyShopsPage from "./pages/HobbyShopsPage";
import CartPage from "./pages/CartPage";
import AccountPage from "./pages/AccountPage";
import AdminPage from "./pages/AdminPage";
import AboutPage from "./pages/AboutPage";
import { ManageClubEventsPage, ManageClubsPage } from "./pages/ManagePage";
import ManageNewsPage from "./pages/ManageNewsPage";
import ManageHobbyShopsPage from "./pages/ManageHobbyShopsPage";
import ManageTracksPage from "./pages/ManageTracksPage";
import ManageStreamPage from "./pages/ManageStreamPage";
import ManageAdvancedPage from "./pages/ManageAdvancedPage";
import { ClubThemeRoot } from "./lib/clubTheme";
import LivePage from "./pages/LivePage";
import PlatformArticlePage from "./pages/PlatformArticlePage";
import { ManageEventPage, ManageRacePage } from "./pages/ManageEventPage";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  if (isLoading) return <p style={{ padding: "2rem" }}>Loading…</p>;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

/** Nav-bar account menu: a single person-lines-fill icon (pinned as the
 *  rightmost item in the nav) as the trigger, a dropdown of Profile/
 *  Vehicles/Registrations/Uploads/(Manage, Admin, if applicable)/Logout
 *  below it, each paired
 *  with an icon. Logout sits last in the list rather than its own
 *  standalone nav icon. Dropdown labels collapse to icon-only on narrow
 *  viewports (see styles.css). */
function AccountMenu() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [managesClubs, setManagesClubs] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // "Manage" shows for club/event managers. Admins always see it (they can
  // manage every club), so only non-admins need the lookup.
  useEffect(() => {
    if (!user || user.isAdmin) return;
    getMe()
      .then((res) => setManagesClubs((res.managedClubCount ?? 0) > 0))
      .catch(() => setManagesClubs(false));
  }, [user?.sub, user?.isAdmin]);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  if (!user) return null;

  function go(tab: string) {
    setOpen(false);
    navigate(`/account?tab=${tab}`);
  }

  function goManage() {
    setOpen(false);
    navigate("/manage");
  }

  function goAdmin() {
    setOpen(false);
    navigate("/admin");
  }

  function handleSignOut() {
    setOpen(false);
    signOut();
  }

  return (
    <div className="account-menu" ref={menuRef}>
      <button
        type="button"
        className="account-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        title="Account"
        aria-label="Account"
      >
        <PersonLinesFillIcon />
      </button>
      {open && (
        <div className="account-dropdown">
          <button type="button" onClick={() => go("profile")}>
            <span className="dd-label">Profile</span> <FilePersonIcon />
          </button>
          <button type="button" onClick={() => go("vehicles")}>
            <span className="dd-label">Vehicles</span> <CarFrontFillIcon />
          </button>
          <button type="button" onClick={() => go("registrations")}>
            <span className="dd-label">Registrations</span> <Calendar3RangeFillIcon />
          </button>
          <button type="button" onClick={() => go("uploads")}>
            <span className="dd-label">Uploads</span> <FileEarmarkArrowUpIcon />
          </button>
          {(user.isAdmin || managesClubs) && (
            <button type="button" onClick={goManage}>
              <span className="dd-label">Manage</span> <ClipboardCheckIcon />
            </button>
          )}
          {user.isAdmin && (
            <button type="button" onClick={goAdmin}>
              <span className="dd-label">Admin</span> <KeyFillIcon />
            </button>
          )}
          <button type="button" onClick={handleSignOut}>
            <span className="dd-label">Logout</span> <BoxArrowRightIcon />
          </button>
        </div>
      )}
    </div>
  );
}

/** Green cart icon shown in the nav only once there's something in it. */
function CartIndicator() {
  const { items } = useCart();
  const navigate = useNavigate();
  if (!items.length) return null;
  return (
    <button
      type="button"
      className="cart-indicator"
      onClick={() => navigate("/cart")}
      title="View cart"
      aria-label="View cart"
    >
      <CartFillIcon />
    </button>
  );
}

// The service site's fallback, from before clubs had their own settings.
// A club's site uses its Manage -> Streaming settings instead.
const DEFAULT_LIVERC_WATCH_URL = "https://hankperryrc.liverc.com/live/";
const LIVESTREAM_POLL_MS = 60_000;

/** Gray camera icon that turns red when LiveRC reports the track is
 *  currently streaming (polled -- see getLivestreamStatus). On a club's site
 *  it opens the club's /live page (its embedded players); elsewhere it links
 *  out to the LiveRC watch page. */
function LivestreamIndicator() {
  const { club, loading } = useSiteClub();
  const [live, setLive] = useState(false);
  // Only poll when there's a LiveRC page to ask about.
  const pollable = !loading && (!club || !!club.stream?.liveRcUrl);

  useEffect(() => {
    if (!pollable) return;
    let cancelled = false;
    function check() {
      getLivestreamStatus(club?.clubId)
        .then((res) => { if (!cancelled) setLive(res.live); })
        .catch(() => {});
    }
    check();
    const id = setInterval(check, LIVESTREAM_POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [pollable, club?.clubId]);

  const label = live ? "Live now" : club ? `${club.name} live stream` : "Not currently streaming";
  const className = `livestream-indicator${live ? " live" : ""}`;
  if (club) {
    return (
      <Link to="/live" className={className} title={label} aria-label={label}>
        <CameraVideoIcon />
      </Link>
    );
  }
  return (
    <a
      href={DEFAULT_LIVERC_WATCH_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      title={live ? "Live now on LiveRC" : label}
      aria-label={live ? "Live now on LiveRC" : label}
    >
      <CameraVideoIcon />
    </a>
  );
}

function Nav() {
  const { user } = useAuth();
  const { club } = useSiteClub();
  // On a club's own domain the wordmark is the club's name; RC Race HQ on
  // the service site.
  const brand = club?.name ?? "RC Race HQ";
  return (
    <nav>
      <Link to="/" className="nav-icon-link brand-chrome" title="Home" aria-label="Home">
        <span className="brand-text">{brand}</span>
        <HouseFillIcon className="brand-icon-mobile" />
      </Link>
      <NavLink to="/events" className="nav-icon-link" title="Events" aria-label="Events">
        <Calendar3RangeFillIcon />
      </NavLink>
      {/* News, hobby shops and the live stream belong to a club, so they
          only show on a club's own domain, not the RC Race HQ service site. */}
      {club && (
        <NavLink to="/news" className="nav-icon-link" title="News" aria-label="News">
          <NewspaperIcon />
        </NavLink>
      )}
      <NavLink to="/locations" className="nav-icon-link" title="Locations" aria-label="Locations">
        <GeoAltIcon />
      </NavLink>
      {club && (
        <NavLink to="/hobby-shops" className="nav-icon-link" title="Hobby Shops" aria-label="Hobby Shops">
          <ShopIcon />
        </NavLink>
      )}
      {club && <LivestreamIndicator />}
      <NavLink to="/about" className="nav-icon-link" title="About/Contact" aria-label="About/Contact">
        <PersonRaisedHandIcon />
      </NavLink>
      <div className="spacer" />
      {user ? (
        <>
          <CartIndicator />
          <AccountMenu />
        </>
      ) : (
        <Link to="/login">Sign in</Link>
      )}
    </nav>
  );
}

export default function App() {
  // Safety net: without this, dropping a file anywhere outside a page's own
  // drop zone (e.g. onto the nav bar) makes the browser navigate away to
  // open it instead of doing nothing. Ported from BackFriend_FullStack's App.tsx.
  useEffect(() => {
    const prevent = (e: DragEvent) => e.preventDefault();
    window.addEventListener("dragover", prevent);
    window.addEventListener("drop", prevent);
    return () => {
      window.removeEventListener("dragover", prevent);
      window.removeEventListener("drop", prevent);
    };
  }, []);

  return (
    <ClubThemeRoot>
      <Nav />
      <main>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<CallbackPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/platform/:slug" element={<PlatformArticlePage />} />
          <Route path="/" element={<HomePage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/:eventId" element={<EventDetailPage />} />
          <Route path="/news" element={<ArticlesPage />} />
          <Route path="/news/:articleId" element={<ArticleDetailPage />} />
          <Route path="/locations" element={<LocationsPage />} />
          <Route path="/hobby-shops" element={<HobbyShopsPage />} />
          <Route
            path="/cart"
            element={
              <RequireAuth>
                <CartPage />
              </RequireAuth>
            }
          />
          <Route
            path="/account"
            element={
              <RequireAuth>
                <AccountPage />
              </RequireAuth>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireAuth>
                <AdminPage />
              </RequireAuth>
            }
          />
          <Route
            path="/manage"
            element={
              <RequireAuth>
                <ManageClubsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/manage/:clubId"
            element={
              <RequireAuth>
                <ManageClubEventsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/manage/:clubId/events/:eventId"
            element={
              <RequireAuth>
                <ManageEventPage />
              </RequireAuth>
            }
          />
          <Route
            path="/manage/:clubId/events/:eventId/heats/:heatId"
            element={
              <RequireAuth>
                <ManageRacePage />
              </RequireAuth>
            }
          />
          <Route
            path="/manage/:clubId/news"
            element={
              <RequireAuth>
                <ManageNewsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/manage/:clubId/hobby-shops"
            element={
              <RequireAuth>
                <ManageHobbyShopsPage />
              </RequireAuth>
            }
          />
          <Route
            path="/manage/:clubId/tracks"
            element={
              <RequireAuth>
                <ManageTracksPage />
              </RequireAuth>
            }
          />
          <Route
            path="/manage/:clubId/stream"
            element={
              <RequireAuth>
                <ManageStreamPage />
              </RequireAuth>
            }
          />
          <Route
            path="/manage/:clubId/advanced"
            element={
              <RequireAuth>
                <ManageAdvancedPage />
              </RequireAuth>
            }
          />
          <Route path="/live" element={<LivePage />} />
          <Route path="*" element={<p>Not found.</p>} />
        </Routes>
      </main>
      <footer className="site-footer">
        <div>
          <a href="/privacy.html">Privacy &amp; Terms</a>
          <span className="muted"> · </span>
          <a href="/data-deletion.html">Data Deletion</a>
        </div>
        <div className="muted" style={{ marginTop: "0.5rem" }}>
          Architected by{" "}
          <a href="https://www.sturgeon.tech" target="_blank" rel="noopener noreferrer">
            Sturgeon Technologies
          </a>
        </div>
      </footer>
    </ClubThemeRoot>
  );
}
